<template>
    <v-app-bar
        dark
        clipped
        elevation="0"
        color="#232323"
        height="56px"
        fixed
        class="topbar"
        style="z-index: 100000;"
    >
        <v-btn icon class="included mr-1" :title="drawer ? 'Close menu' : 'Open menu'" @click.stop="setDrawer">
            <v-icon>{{ drawer ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
        </v-btn>
        <router-link to="/" class="topbar__brand">
            <img class="topbar__mark" src="@/assets/mark.svg" alt="">
            <span class="topbar__brand-text">Commercial League</span>
            <span class="topbar__brand-tag">CMS</span>
        </router-link>
        <template v-if="currentSection">
            <span class="topbar__separator" />
            <div class="topbar__crumbs">
                <v-icon small class="topbar__crumb-icon mr-2">{{ currentSection.icon }}</v-icon>
                <router-link :to="currentSection.to" class="topbar__crumb">{{ currentSection.title }}</router-link>
                <template v-if="currentAction">
                    <v-icon small class="mx-1">mdi-chevron-right</v-icon>
                    <span class="topbar__crumb topbar__crumb--current">{{ currentAction }}</span>
                </template>
            </div>
        </template>
        <v-spacer />
        <request-notifications />
        <v-menu offset-y left min-width="200">
            <template v-slot:activator="{ on, attrs }">
                <v-btn text class="topbar__user px-2" v-bind="attrs" v-on="on">
                    <v-avatar size="32" class="topbar__avatar mr-2">
                        <v-icon small color="white">mdi-basketball</v-icon>
                    </v-avatar>
                    <span v-if="roleLabel" class="topbar__role d-none d-sm-inline">{{ roleLabel }}</span>
                    <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
            </template>
            <v-card>
                <v-list dense>
                    <v-list-item>
                        <v-list-item-content>
                            <v-list-item-title class="font-weight-bold">Signed in</v-list-item-title>
                            <v-list-item-subtitle v-if="roleLabel">{{ roleLabel }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item link @click="logout">
                        <v-list-item-icon class="mr-3">
                            <v-icon small color="red darken-1">mdi-logout</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="red--text text--darken-1">Logout</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-menu>
    </v-app-bar>
</template>

<script>
    import userService from '@/services/user'
    import moment from 'moment'
    import { findMenuItem } from '@/utils/menu'
    import RequestNotifications from '@/components/RequestNotifications'

    const ACTION_LABELS = { create: 'New', edit: 'Edit', show: 'View' }

    export default {
        components: { RequestNotifications },
        data() {
            return {
                active: false,
                icon: 'mdi-view-grid-outline',
                tokenExpiration: '',
                interval: null,
                remainingMinutes: null
            }
        },
        computed: {
            currentSection() {
                return findMenuItem(this.$route.path)
            },
            currentAction() {
                return ACTION_LABELS[this.$route.params.mode] || null
            },
            // ROLE_ADMIN -> Admin
            roleLabel() {
                const role = this.$store.getters.getUserRole
                if (!role) return null
                const name = role.replace(/^ROLE_/, '').toLowerCase()
                return name.charAt(0).toUpperCase() + name.slice(1)
            },
            refreshTokenExpiration() {
                return this.$store.getters.getRefreshTokenExpiration
            },
            drawer: {
                get() {
                    return this.$store.getters.getDrawer
                },
                set(value) {
                    return value
                }
            }
        },
        watch: {
            tokenExpiration() {
                this.remainingMinutes = moment(this.refreshTokenExpiration).subtract(30, 'minutes').diff(moment(), 'minutes')
            }
        },
        created() {
            this.setExpireTokenInterval()
        },
        methods: {
            setDrawer() {
                this.$store.dispatch('setDrawer', !this.drawer)
            },
            logout() {
                this.$store.dispatch('logout')
                this.$router.push(`/login`)
            },
            calculateTokenExpiration(newEpoch) {
                if (newEpoch) {
                    const expiration = moment(newEpoch)
                    const diff = expiration.diff(moment().utc().subtract(30, 'minutes'))
                    this.tokenExpiration = moment.utc(diff).format('mm:ss')
                }
            },
            refreshToken() {
                userService.refreshToken({
                    tokenValue: this.$store.getters.getRefreshToken
                }).then(resp => {
                    this.$store.dispatch('setRefreshToken', resp.data.refreshToken)
                    this.$store.dispatch('setRefreshTokenExpiration', resp.data.refreshTokenExpiration)
                    this.$store.dispatch('setToken', resp.data.token)
                })
            },
            setExpireTokenInterval() {
                if (this.interval) {
                    clearInterval(this.interval)
                }
                this.calculateTokenExpiration(this.refreshTokenExpiration)
                this.interval = setInterval(() => {
                    this.calculateTokenExpiration(this.refreshTokenExpiration)
                }, 1000)
            }
        }
    }
</script>

<style>
    /* charcoal bar of the website with its teal-to-blue line underneath */
    .topbar {
        border-bottom: 0 !important;
    }

    .topbar::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -3px;
        height: 3px;
        background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
    }

    .topbar__brand {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: #fff !important;
    }

    .topbar__mark {
        width: 30px;
        height: 30px;
        display: block;
    }

    .topbar__brand-text {
        font-family: 'Squada One', Arial, sans-serif;
        font-size: 1.45rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        line-height: 1;
    }

    .topbar__brand-tag {
        padding: 2px 7px;
        border-radius: 4px;
        background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
        color: #fff;
        font-size: 0.65rem;
        font-weight: 700;
        letter-spacing: 0.12em;
    }

    .topbar__separator {
        width: 1px;
        height: 24px;
        margin: 0 16px;
        background: rgba(255, 255, 255, 0.18);
    }

    .topbar__crumbs {
        display: flex;
        align-items: center;
        min-width: 0;
        font-size: 0.9rem;
    }

    .topbar__crumb-icon {
        color: #03e5b7 !important;
    }

    .topbar__crumb {
        color: rgba(255, 255, 255, 0.65) !important;
        text-decoration: none;
        white-space: nowrap;
    }

    .topbar__crumb:hover {
        color: #fff !important;
    }

    .topbar__crumb--current {
        color: #fff !important;
        font-weight: 600;
    }

    .topbar__avatar {
        background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    }

    .topbar__user {
        text-transform: none !important;
        letter-spacing: normal !important;
    }

    .topbar__role {
        font-weight: 600;
        margin-right: 4px;
    }

    @media (max-width: 600px) {
        .topbar__brand-text,
        .topbar__brand-tag,
        .topbar__separator,
        .topbar__crumbs {
            display: none;
        }
    }
</style>
